import { useRef, useState, useEffect } from "react";
import { useSearchParams, useLocation } from "react-router-dom";
import SolutionsHero from "./components/SolutionsHero";
import SolutionsBlocks from "./components/SolutionsBlocks";
import SolutionsProcess from "./components/SolutionsProcess";
import SolutionsCta from "./components/SolutionsCta";
import { useSEO, buildBreadcrumbLd } from "@/utils/seo";
import type { SEOOptions } from "@/utils/seo";
import { ACTIVE_CATEGORY_SET, resolveActiveCategory } from "@/utils/solutionCategory";
import FaqBlock from "@/components/feature/FaqBlock";

/** 分类标识不再限定为固定字面量联合类型，随 ACTIVE_CATEGORY_SET / 接口分类列表变化 */
type TabId = string;

/** 由 ACTIVE_CATEGORY_SET 驱动的合法 Tab 列表（当前为 4 个生效分类） */
const VALID_TABS: TabId[] = [...ACTIVE_CATEGORY_SET];

const TAB_SEO: Record<TabId, { title: string; description: string; keywords: string[]; name: string }> = {
  "info-security": {
    name: "信息安全",
    title: "信息安全综合防护解决方案",
    description:
      "欧格登信息安全综合防护方案，覆盖防火墙、入侵检测、零信任架构、SOC安全运营、等保合规、数据安全，助力企业抵御勒索病毒、APT攻击和内部威胁。",
    keywords: ["信息安全方案", "零信任", "SOC安全运营", "等保三级", "数据安全", "勒索病毒防护"],
  },
  "it-infrastructure": {
    name: "IT基础架构",
    title: "IT基础架构解决方案",
    description:
      "欧格登IT基础架构方案，覆盖数据中心、云计算、超融合、存储备份、网络建设与运维托管，助力企业构建高可用、高性能、可扩展的数字化底座。",
    keywords: ["IT基础架构", "数据中心", "超融合", "云计算", "存储备份", "网络建设"],
  },
  "cloud-xinda": {
    name: "云信达",
    title: "云信达云计算与容灾备份解决方案",
    description:
      "欧格登云信达解决方案，覆盖云主机、云存储、异地容灾、数据备份与恢复、混合云管理平台，助力企业构建稳定可靠、弹性扩展的云端数据服务体系。",
    keywords: ["云信达", "云计算", "容灾备份", "数据备份", "混合云", "云存储", "灾难恢复"],
  },
  "ai-business": {
    name: "AI业务",
    title: "AI数据安全解决方案",
    description:
      "欧格登AI数据安全一体化解决方案：AI场景数据合规、模型安全防护、智能体行为治理与外发管控，私有化部署、数据不出网、全程可审计，面向AI应用上线前、运行中、外发后的全链路安全闭环，助力企业安全地用好AI。",
    keywords: ["AI数据安全", "大模型安全", "AI合规", "模型防护", "智能体治理", "AI资产治理", "数据防泄漏", "MCP安全", "私有化部署", "数据不出网"],
  },
};

/**
 * 裸 `/solutions`（无 `?category=`）的 SEO：分类导览页（SOLUTION_LIST）。
 *
 * 必须与四个分类页的 `TAB_SEO` 分开：裸 `/solutions` 默认激活 info-security Tab，
 * 若仍取 `TAB_SEO["info-security"]`，它与 `/solutions?category=info-security`
 * 会输出逐字符相同的 `<title>`，违反「任意两个 Indexable_URL 的标题不相同」（需求 1.5）。
 *
 * 标题 ≥ 20 字符，`clampTitle()` 不会再追加站点名后缀；描述与关键词只描述四个生效分类，
 * 不含已下线的旧分类字样（需求 13.6）。
 */
const OVERVIEW_SEO = {
  title: "欧格登解决方案总览 - 信息安全、IT基础架构、云信达、AI业务",
  description:
    "欧格登解决方案总览，涵盖信息安全、IT基础架构、云信达与AI业务四大领域：从等保合规、零信任与数据安全，到数据中心、超融合与容灾备份，再到AI数据安全，提供规划设计、建设实施到运维托管的全周期服务。",
  keywords: ["解决方案总览", "欧格登解决方案", "信息安全", "IT基础架构", "云信达", "AI业务", "企业安全解决方案"],
};

export default function GenericSolutionsPage() {
  const [activeTab, setActiveTab] = useState<TabId>("info-security");
  const [searchParams] = useSearchParams();
  const { hash } = useLocation();

  // 有 `?category=` 查询参数时该页面呈现的是 SOLUTION_CATEGORY（某个具体分类），
  // 没有该参数（如裸 `/solutions`）时呈现的是 SOLUTION_LIST（分类导览）。
  // 只有 SOLUTION_CATEGORY 需要 FaqBlock（PAGE_TYPE_META_PRESET.SOLUTION_CATEGORY.renderFaq）。
  const hasCategoryParam = !!searchParams.get("category");

  // 先算出 SEO 取值再调用唯一一次 useSEO()，保证 Hooks 调用顺序恒定。
  // - 有 `?category=`：呈现某个具体分类（SOLUTION_CATEGORY），沿用该 Tab 的 TAB_SEO，
  //   canonical 保留 `?category=`，面包屑 3 层（首页 → 解决方案 → 分类）。
  // - 无 `?category=`（裸 `/solutions`）：呈现分类导览（SOLUTION_LIST），使用总览专属文案，
  //   canonical 为 `/solutions`，面包屑 2 层（首页 → 解决方案）。
  const tabSeo = TAB_SEO[activeTab];
  const seoOptions: SEOOptions = hasCategoryParam
    ? {
        title: tabSeo.title,
        description: tabSeo.description,
        keywords: tabSeo.keywords,
        path: `/solutions?category=${activeTab}`,
        pageType: "SOLUTION_CATEGORY",
        jsonLd: buildBreadcrumbLd([
          { name: "首页", path: "/" },
          { name: "解决方案", path: "/solutions" },
          { name: tabSeo.name, path: `/solutions?category=${activeTab}` },
        ]),
      }
    : {
        title: OVERVIEW_SEO.title,
        description: OVERVIEW_SEO.description,
        keywords: OVERVIEW_SEO.keywords,
        path: "/solutions",
        pageType: "SOLUTION_LIST",
        jsonLd: buildBreadcrumbLd([
          { name: "首页", path: "/" },
          { name: "解决方案", path: "/solutions" },
        ]),
      };
  useSEO(seoOptions);

  const blocksRef = useRef<HTMLDivElement>(null);

  const blockRefs = {
    "info-security": useRef<HTMLDivElement>(null),
    "it-infrastructure": useRef<HTMLDivElement>(null),
    "cloud-xinda": useRef<HTMLDivElement>(null),
    "ai-business": useRef<HTMLDivElement>(null),
  };

  const scrollToBlocks = (delay = 300) => {
    setTimeout(() => {
      const el = blocksRef.current;
      if (el) {
        const offset = 80;
        const top = el.getBoundingClientRect().top + window.scrollY - offset;
        window.scrollTo({ top, behavior: "smooth" });
      }
    }, delay);
  };

  // 支持 ?category=xxx 和 #xxx 两种方式切换 tab，并兼容旧分类标识 intelligent-building
  useEffect(() => {
    const category = searchParams.get("category");
    const hashTab = hash ? hash.slice(1) : null;
    const rawTarget = category || hashTab;

    const resolved = resolveActiveCategory(rawTarget, VALID_TABS);
    setActiveTab(resolved);

    const isLegacy = rawTarget === "intelligent-building";
    const isInvalid = !!rawTarget && !isLegacy && !VALID_TABS.includes(rawTarget);

    if (isLegacy) {
      // 旧标识（?category= 或 #hash 形式）统一改写为 ?category=it-infrastructure，
      // 保留其余查询参数，以替换历史记录方式更新地址（不新增历史条目）
      const params = new URLSearchParams(window.location.search);
      params.set("category", resolved);
      const newUrl = `${window.location.pathname}?${params.toString()}`;
      window.history.replaceState(null, "", newUrl);
      scrollToBlocks(300);
    } else if (isInvalid) {
      // 非法分类标识回落为 info-security，同样以替换历史记录方式更新地址，
      // 但保持首屏 Hero 滚动位置，不触发滚动
      const params = new URLSearchParams(window.location.search);
      params.set("category", resolved);
      const newUrl = `${window.location.pathname}?${params.toString()}`;
      window.history.replaceState(null, "", newUrl);
    } else if (rawTarget) {
      // 有效分类标识，地址已正确，滚动到对应内容区顶部
      scrollToBlocks(300);
    }
  }, [searchParams, hash]);

  const handleScrollTo = (id: string) => {
    setActiveTab(id as TabId);
    scrollToBlocks(50);
  };

  return (
    <main className="min-h-screen">
      <SolutionsHero onScrollTo={handleScrollTo} />
      <div ref={blocksRef}>
        <SolutionsBlocks blockRefs={blockRefs} activeTab={activeTab} onTabChange={setActiveTab} />
      </div>
      <SolutionsProcess />
      <SolutionsCta />
      {hasCategoryParam && <FaqBlock scope="SOLUTION_CATEGORY" />}
    </main>
  );
}
